import { useEffect, useState, useCallback } from "react";
import { Loader2, CheckCircle2, XCircle, AlertTriangle, Copy, RefreshCw } from "lucide-react";
import StaffHeader from "@/components/StaffHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { formatDate } from "@/lib/dateUtils";
import { useToast } from "@/hooks/use-toast";

const OUTCOME_STYLES = {
  processed: "bg-amtm-success/15 text-amtm-success",
  skipped_belongs_to_guardian: "bg-amtm-info/15 text-amtm-info",
  unattributed_payment_recorded: "bg-amber-500/15 text-amber-700",
  failed_payment_recorded: "bg-destructive/15 text-destructive",
  signature_invalid: "bg-destructive/15 text-destructive",
  duplicate_ignored: "bg-amtm-textMuted/15 text-amtm-textMuted",
  no_matching_subscription: "bg-amber-500/15 text-amber-700",
  no_matching_order: "bg-amber-500/15 text-amber-700",
  unhandled_event_type: "bg-amtm-textMuted/15 text-amtm-textMuted",
  refund_processed: "bg-amtm-info/15 text-amtm-info",
};

function EnvVarRow({ name, entry }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-amtm-border last:border-0" data-testid={`env-var-row-${name}`}>
      <span className="text-sm font-mono text-amtm-text">{name}</span>
      <div className="flex items-center gap-2">
        {entry.warning && <AlertTriangle className="h-4 w-4 text-amber-500" data-testid={`env-var-warning-${name}`} />}
        {entry.set ? (
          <span className="flex items-center gap-1 text-xs font-semibold text-amtm-success" data-testid={`env-var-status-${name}`}>
            <CheckCircle2 className="h-4 w-4" /> Set
          </span>
        ) : (
          <span className="flex items-center gap-1 text-xs font-semibold text-destructive" data-testid={`env-var-status-${name}`}>
            <XCircle className="h-4 w-4" /> Missing
          </span>
        )}
      </div>
    </div>
  );
}

function WebhookUrlCard({ title, info, testId }) {
  const { toast } = useToast();
  const copy = () => {
    navigator.clipboard.writeText(info.url);
    toast({ title: "Webhook URL copied" });
  };
  return (
    <div className="bg-amtm-surface border border-amtm-border rounded-xl p-4" data-testid={testId}>
      <p className="text-sm font-semibold text-amtm-text mb-2">{title}</p>
      <div className="flex items-center gap-2 mb-3">
        <code className="text-xs bg-amtm-bg rounded px-2 py-1.5 flex-1 truncate" data-testid={`${testId}-url`}>{info.url}</code>
        <Button size="sm" variant="outline" onClick={copy} data-testid={`${testId}-copy-button`}>
          <Copy className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {info.accepted_events.map((e) => (
          <Badge key={e} variant="outline" className="text-[10px]">{e}</Badge>
        ))}
      </div>
    </div>
  );
}

export default function Diagnostics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [endpointFilter, setEndpointFilter] = useState("");

  const fetchDiagnostics = useCallback(async (isRefresh) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    try {
      const params = endpointFilter ? { endpoint: endpointFilter } : {};
      const { data: res } = await api.get("/admin/diagnostics", { params });
      setData(res);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [endpointFilter]);

  useEffect(() => {
    fetchDiagnostics(false);
  }, [fetchDiagnostics]);

  if (loading || !data) {
    return (
      <div className="min-h-screen bg-amtm-bg">
        <StaffHeader />
        <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-amtm-teal" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-amtm-bg" data-testid="admin-diagnostics-page">
      <StaffHeader />
      <div className="max-w-4xl mx-auto px-4 py-6 pb-24 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl text-amtm-text" data-testid="diagnostics-page-title">Diagnostics</h1>
            <p className="text-sm text-amtm-textMuted mt-1">Live health of the Razorpay webhooks + Meta CAPI relay. Values are never shown, only set/missing status.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchDiagnostics(true)} disabled={refreshing} data-testid="diagnostics-refresh-button">
            {refreshing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-amtm-surface border border-amtm-border rounded-xl p-4 text-center" data-testid="diagnostics-unattributed-count">
            <p className="text-2xl font-display text-amtm-text">{data.unattributed_payments_count_30d}</p>
            <p className="text-xs text-amtm-textMuted mt-1">Unattributed payments (last 30 days)</p>
          </div>
          <div className="bg-amtm-surface border border-amtm-border rounded-xl p-4 text-center" data-testid="diagnostics-missing-details-count">
            <p className="text-2xl font-display text-amtm-text">{data.donations_missing_details_count_30d}</p>
            <p className="text-xs text-amtm-textMuted mt-1">Case donations missing donor details (last 30 days)</p>
          </div>
          <div className="bg-amtm-surface border border-amtm-border rounded-xl p-4 text-center" data-testid="diagnostics-guardian-missing-details-count">
            <p className="text-2xl font-display text-amtm-text">{data.guardian_donations_missing_details_count_30d}</p>
            <p className="text-xs text-amtm-textMuted mt-1">Guardian donations missing donor details (last 30 days)</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <WebhookUrlCard title="Case Webhook" info={data.webhook_urls.case_webhook} testId="diagnostics-case-webhook-url" />
          <WebhookUrlCard title="Guardian Webhook" info={data.webhook_urls.guardian_webhook} testId="diagnostics-guardian-webhook-url" />
        </div>

        <div className="bg-amtm-surface border border-amtm-border rounded-xl p-4" data-testid="diagnostics-env-status">
          <p className="text-sm font-semibold text-amtm-text mb-2">Environment Variables</p>
          {Object.entries(data.env_status).map(([name, entry]) => (
            <EnvVarRow key={name} name={name} entry={entry} />
          ))}
        </div>

        <div className="bg-amtm-surface border border-amtm-border rounded-xl p-4" data-testid="diagnostics-webhook-logs">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <p className="text-sm font-semibold text-amtm-text">Recent Webhook Events (last 50)</p>
            <div className="flex gap-1.5">
              {[
                { key: "", label: "All" },
                { key: "case_webhook", label: "Case" },
                { key: "guardian_webhook", label: "Guardian" },
              ].map((f) => (
                <Button
                  key={f.key}
                  size="sm"
                  variant={endpointFilter === f.key ? "default" : "outline"}
                  onClick={() => setEndpointFilter(f.key)}
                  className={endpointFilter === f.key ? "bg-amtm-teal text-white hover:bg-amtm-teal/90" : ""}
                  data-testid={`diagnostics-webhook-filter-${f.label.toLowerCase()}`}
                >
                  {f.label}
                </Button>
              ))}
            </div>
          </div>
          {data.webhook_logs.length === 0 ? (
            <p className="text-sm text-amtm-textMuted text-center py-6" data-testid="diagnostics-webhook-logs-empty">No webhook events logged yet.</p>
          ) : (
            <div className="space-y-2 max-h-[420px] overflow-y-auto">
              {data.webhook_logs.map((log) => (
                <div key={log.id} className="flex items-center justify-between gap-3 border border-amtm-border rounded-lg px-3 py-2 flex-wrap" data-testid={`webhook-log-row-${log.id}`}>
                  <div className="min-w-0">
                    <p className="text-xs font-mono text-amtm-text">{log.endpoint} · {log.event_type || "(no event)"}</p>
                    <p className="text-[11px] text-amtm-textMuted">{formatDate(log.received_at)} · status {log.response_status}</p>
                    {log.error_message && <p className="text-[11px] text-destructive">{log.error_message}</p>}
                  </div>
                  <Badge className={OUTCOME_STYLES[log.outcome] || "bg-amtm-textMuted/15 text-amtm-textMuted"} data-testid={`webhook-log-outcome-${log.id}`}>
                    {log.outcome}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-amtm-surface border border-amtm-border rounded-xl p-4" data-testid="diagnostics-meta-capi-logs">
          <p className="text-sm font-semibold text-amtm-text mb-3">Recent Meta CAPI Responses (last 20)</p>
          {data.meta_capi_logs.length === 0 ? (
            <p className="text-sm text-amtm-textMuted text-center py-6" data-testid="diagnostics-meta-capi-logs-empty">No Meta CAPI events sent yet.</p>
          ) : (
            <div className="space-y-2 max-h-[420px] overflow-y-auto">
              {data.meta_capi_logs.map((log) => (
                <div key={log.id} className="flex items-center justify-between gap-3 border border-amtm-border rounded-lg px-3 py-2 flex-wrap" data-testid={`meta-capi-log-row-${log.id}`}>
                  <div className="min-w-0">
                    <p className="text-xs font-mono text-amtm-text">{log.payment_id} · {log.donation_type}</p>
                    <p className="text-[11px] text-amtm-textMuted">{formatDate(log.created_at)} · HTTP {log.status_code ?? "—"}</p>
                  </div>
                  <Badge className={log.success ? "bg-amtm-success/15 text-amtm-success" : "bg-destructive/15 text-destructive"} data-testid={`meta-capi-log-status-${log.id}`}>
                    {log.success ? "Success" : "Failed"}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
